import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import api from '@/utils/api';
import { getUser, setUser } from '@/utils/auth';
import { toast } from 'sonner';
import { ArrowLeft, Camera, User, Mail, Phone, FileText, Save, Loader2, Award, Upload, Trash2, ExternalLink, File, Facebook, Instagram } from 'lucide-react';
import { motion } from 'framer-motion';
import { uploadCertificateToR2 } from '@/utils/s3Upload';

// Custom TikTok icon (lucide-react doesn't have it)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const TeacherProfilePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const certInputRef = useRef(null);
  const user = getUser();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isUploadingCert, setIsUploadingCert] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [certificates, setCertificates] = useState([]);
  const [showCertDialog, setShowCertDialog] = useState(false);
  const [certTitle, setCertTitle] = useState('');
  const [certFile, setCertFile] = useState(null);
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    avatar_url: null,
    facebook_url: '',
    tiktok_url: '',
    instagram_url: ''
  });
  const [socialUrlErrors, setSocialUrlErrors] = useState({});

  // URL validation helper
  const isValidUrl = (url) => {
    if (!url || url.trim() === '') return true; // Empty is valid (optional)
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateSocialUrls = () => {
    const errors = {};
    if (profile.facebook_url && !isValidUrl(profile.facebook_url)) {
      errors.facebook_url = 'Please enter a valid URL';
    }
    if (profile.tiktok_url && !isValidUrl(profile.tiktok_url)) {
      errors.tiktok_url = 'Please enter a valid URL';
    }
    if (profile.instagram_url && !isValidUrl(profile.instagram_url)) {
      errors.instagram_url = 'Please enter a valid URL';
    }
    setSocialUrlErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        avatar_url: user.avatar_url || null,
        facebook_url: user.facebook_url || '',
        tiktok_url: user.tiktok_url || '',
        instagram_url: user.instagram_url || ''
      });
    }
    fetchProfile();
    fetchCertificates();
  }, []);

  const fetchProfile = async () => {
    setIsLoadingProfile(true);
    try {
      const response = await api.get('/auth/me');
      const profileData = {
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        bio: response.data.bio || '',
        avatar_url: response.data.avatar_url || null,
        facebook_url: response.data.facebook_url || '',
        tiktok_url: response.data.tiktok_url || '',
        instagram_url: response.data.instagram_url || ''
      };
      setProfile(profileData);
      
      // Sync localStorage with fresh data from API
      const currentUser = getUser();
      if (currentUser) {
        setUser({ ...currentUser, ...response.data });
      }
    } catch (error) {
      // Use cached user data
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const fetchCertificates = async () => {
    try {
      const response = await api.get('/teacher/certificates');
      setCertificates(response.data);
    } catch (error) {
      console.error('Failed to fetch certificates:', error);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    // Validate social URLs before saving
    if (!validateSocialUrls()) {
      toast.error('Please fix the URL errors before saving');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const response = await api.put('/auth/profile', {
        name: profile.name,
        phone: profile.phone,
        bio: profile.bio,
        facebook_url: profile.facebook_url || null,
        tiktok_url: profile.tiktok_url || null,
        instagram_url: profile.instagram_url || null
      });
      
      // Update local storage
      const updatedUser = { ...user, ...response.data.user };
      setUser(updatedUser);
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setIsUploadingAvatar(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/auth/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update profile with new avatar URL (add cache buster)
      const avatarUrlWithCacheBuster = `${response.data.avatar_url}${response.data.avatar_url.includes('?') ? '&' : '?'}v=${Date.now()}`;
      setProfile(prev => ({ ...prev, avatar_url: avatarUrlWithCacheBuster }));
      const updatedUser = { ...user, avatar_url: avatarUrlWithCacheBuster };
      setUser(updatedUser);
      
      // Refresh profile to ensure consistency
      await fetchProfile();
      
      toast.success('Profile photo updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleCertFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a PDF or image file (JPEG, PNG, WebP)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }

    setCertFile(file);
  };

  const handleUploadCertificate = async () => {
    if (!certFile || !certTitle.trim()) {
      toast.error('Please provide a title and select a file');
      return;
    }

    setIsUploadingCert(true);
    setUploadProgress(0);

    try {
      // Use direct R2 upload with presigned URLs
      await uploadCertificateToR2(certFile, certTitle, (progress) => {
        setUploadProgress(progress);
      });

      toast.success('Certificate uploaded successfully!');
      setShowCertDialog(false);
      setCertTitle('');
      setCertFile(null);
      setUploadProgress(0);
      fetchCertificates();
    } catch (error) {
      toast.error(error.message || 'Failed to upload certificate');
    } finally {
      setIsUploadingCert(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteCertificate = async (certId) => {
    if (!confirm('Are you sure you want to delete this certificate?')) return;

    try {
      await api.delete(`/teacher/certificates/${certId}`);
      toast.success('Certificate deleted');
      fetchCertificates();
    } catch (error) {
      toast.error('Failed to delete certificate');
    }
  };

  const getAvatarUrl = () => {
    if (profile.avatar_url) {
      if (profile.avatar_url.startsWith('http')) {
        return profile.avatar_url;
      }
      // For uploaded avatars, construct full URL with backend
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      // Avatar URLs are like /api/files/avatars/xxx.jpg
      return `${baseUrl}${profile.avatar_url}?auth=${token}`;
    }
    return null;
  };

  const getFileUrl = (url, presignedUrl = null) => {
    // If a presigned URL is available (from R2), use it directly
    if (presignedUrl) return presignedUrl;
    if (!url) return '';
    if (url.startsWith('http')) return url;
    // For uploaded files, construct full URL with backend and auth token
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}${url}?auth=${token}`;
  };

  const getCertificateUrl = (cert) => {
    // Prefer presigned URL for R2 certificates
    if (cert.presigned_url) return cert.presigned_url;
    return getFileUrl(cert.file_url);
  };

  return (
    <div className="min-h-screen py-8 px-6" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/teacher/dashboard')}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          {/* Profile Card */}
          <Card style={{ borderColor: '#E3E1DB' }}>
            <CardHeader>
              <CardTitle 
                className="text-2xl"
                style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
              >
                My Profile
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveProfile} className="space-y-6">
                {/* Avatar Section */}
                <div className="flex flex-col items-center mb-6">
                  <div 
                    className="relative cursor-pointer group"
                    onClick={handleAvatarClick}
                  >
                    <div 
                      className="w-32 h-32 rounded-full overflow-hidden border-4 flex items-center justify-center"
                      style={{ borderColor: '#163329', backgroundColor: '#F0EFEA' }}
                    >
                      {(isLoadingProfile || isUploadingAvatar) ? (
                        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#163329' }} />
                      ) : getAvatarUrl() ? (
                        <img src={getAvatarUrl()} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-16 w-16" style={{ color: '#5C635A' }} />
                      )}
                    </div>
                    <div 
                      className="absolute bottom-0 right-0 p-2 rounded-full"
                      style={{ backgroundColor: '#163329' }}
                    >
                      <Camera className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <p className="text-sm mt-2" style={{ color: '#5C635A' }}>
                    Click to upload a profile photo
                  </p>
                </div>

                {/* Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <User className="h-4 w-4" style={{ color: '#5C635A' }} />
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    value={profile.name}
                    onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email Field (Read-only) */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" style={{ color: '#5C635A' }} />
                    Email Address
                  </Label>
                  <Input id="email" value={profile.email} disabled className="bg-gray-50" />
                  <p className="text-xs" style={{ color: '#5C635A' }}>Email cannot be changed</p>
                </div>

                {/* Phone Field */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" style={{ color: '#5C635A' }} />
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    value={profile.phone}
                    onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Enter your phone number"
                  />
                </div>

                {/* Bio Field */}
                <div className="space-y-2">
                  <Label htmlFor="bio" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" style={{ color: '#5C635A' }} />
                    Bio / About Me
                  </Label>
                  <Textarea
                    id="bio"
                    value={profile.bio}
                    onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell students about your experience and expertise..."
                    rows={4}
                  />
                </div>

                {/* Social Links Section */}
                <div className="space-y-4 pt-4 border-t" style={{ borderColor: '#E3E1DB' }}>
                  <h3 className="font-medium flex items-center gap-2" style={{ color: '#163329' }}>
                    Social Media Links
                  </h3>
                  
                  {/* Facebook URL */}
                  <div className="space-y-2">
                    <Label htmlFor="facebook_url" className="flex items-center gap-2">
                      <Facebook className="h-4 w-4" style={{ color: '#1877F2' }} />
                      Facebook
                    </Label>
                    <Input
                      id="facebook_url"
                      type="url"
                      value={profile.facebook_url}
                      onChange={(e) => {
                        setProfile(prev => ({ ...prev, facebook_url: e.target.value }));
                        setSocialUrlErrors(prev => ({ ...prev, facebook_url: null }));
                      }}
                      placeholder="https://facebook.com/yourprofile"
                      className={socialUrlErrors.facebook_url ? 'border-red-500' : ''}
                    />
                    {socialUrlErrors.facebook_url && (
                      <p className="text-xs text-red-500">{socialUrlErrors.facebook_url}</p>
                    )}
                  </div>
                  
                  {/* Instagram URL */}
                  <div className="space-y-2">
                    <Label htmlFor="instagram_url" className="flex items-center gap-2">
                      <Instagram className="h-4 w-4" style={{ color: '#E4405F' }} />
                      Instagram
                    </Label>
                    <Input
                      id="instagram_url"
                      type="url"
                      value={profile.instagram_url}
                      onChange={(e) => {
                        setProfile(prev => ({ ...prev, instagram_url: e.target.value }));
                        setSocialUrlErrors(prev => ({ ...prev, instagram_url: null }));
                      }}
                      placeholder="https://instagram.com/yourprofile"
                      className={socialUrlErrors.instagram_url ? 'border-red-500' : ''}
                    />
                    {socialUrlErrors.instagram_url && (
                      <p className="text-xs text-red-500">{socialUrlErrors.instagram_url}</p>
                    )}
                  </div>
                  
                  {/* TikTok URL */}
                  <div className="space-y-2">
                    <Label htmlFor="tiktok_url" className="flex items-center gap-2">
                      <TikTokIcon className="h-4 w-4" />
                      TikTok
                    </Label>
                    <Input
                      id="tiktok_url"
                      type="url"
                      value={profile.tiktok_url}
                      onChange={(e) => {
                        setProfile(prev => ({ ...prev, tiktok_url: e.target.value }));
                        setSocialUrlErrors(prev => ({ ...prev, tiktok_url: null }));
                      }}
                      placeholder="https://tiktok.com/@yourprofile"
                      className={socialUrlErrors.tiktok_url ? 'border-red-500' : ''}
                    />
                    {socialUrlErrors.tiktok_url && (
                      <p className="text-xs text-red-500">{socialUrlErrors.tiktok_url}</p>
                    )}
                  </div>
                </div>

                {/* Save Button */}
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="w-full"
                  
                >
                  {isSaving ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="mr-2 h-4 w-4" />Save Profile</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Certificates Card */}
          <Card style={{ borderColor: '#E3E1DB' }} className="mt-6">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle 
                  className="text-xl flex items-center gap-2"
                  style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
                >
                  <Award className="h-5 w-5" />
                  Certificates & Credentials
                </CardTitle>
                <Dialog open={showCertDialog} onOpenChange={setShowCertDialog}>
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      className="rounded-full"
                      
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Certificate
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                        Upload Certificate
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label htmlFor="cert-title">Certificate Title</Label>
                        <Input
                          id="cert-title"
                          value={certTitle}
                          onChange={(e) => setCertTitle(e.target.value)}
                          placeholder="e.g., Bachelor's in Education, Teaching Certificate"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Certificate File (PDF or Image)</Label>
                        <div 
                          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
                          style={{ borderColor: '#E3E1DB' }}
                          onClick={() => certInputRef.current?.click()}
                        >
                          {certFile ? (
                            <div className="flex items-center justify-center gap-2">
                              <File className="h-5 w-5" style={{ color: '#163329' }} />
                              <span style={{ color: '#163329' }}>{certFile.name}</span>
                            </div>
                          ) : (
                            <div>
                              <Upload className="h-8 w-8 mx-auto mb-2" style={{ color: '#5C635A' }} />
                              <p style={{ color: '#5C635A' }}>Click to select a file</p>
                              <p className="text-xs mt-1" style={{ color: '#5C635A' }}>PDF, JPEG, PNG, WebP (max 10MB)</p>
                            </div>
                          )}
                        </div>
                        <input
                          ref={certInputRef}
                          type="file"
                          accept=".pdf,image/jpeg,image/png,image/webp"
                          onChange={handleCertFileSelect}
                          className="hidden"
                        />
                      </div>
                      <Button
                        onClick={handleUploadCertificate}
                        disabled={isUploadingCert || !certFile || !certTitle.trim()}
                        className="w-full"
                        
                      >
                        {isUploadingCert ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading {uploadProgress > 0 ? `${uploadProgress}%` : '...'}</>
                        ) : (
                          <><Upload className="mr-2 h-4 w-4" />Upload Certificate</>
                        )}
                      </Button>
                      {isUploadingCert && uploadProgress > 0 && (
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%`, backgroundColor: '#163329' }}
                          />
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {certificates.length === 0 ? (
                <div className="text-center py-8">
                  <Award className="h-12 w-12 mx-auto mb-3" style={{ color: '#E3E1DB' }} />
                  <p style={{ color: '#5C635A' }}>No certificates uploaded yet</p>
                  <p className="text-sm mt-1" style={{ color: '#5C635A' }}>
                    Upload your certifications to build trust with students
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {certificates.map((cert) => (
                    <div 
                      key={cert.id}
                      className="flex items-center justify-between p-3 rounded-lg"
                      style={{ backgroundColor: '#F0EFEA' }}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="p-2 rounded-full"
                          style={{ backgroundColor: '#163329' }}
                        >
                          <Award className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="font-medium" style={{ color: '#163329' }}>{cert.title}</p>
                          <p className="text-xs" style={{ color: '#5C635A' }}>
                            Uploaded {new Date(cert.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(getCertificateUrl(cert), '_blank')}
                          title="View certificate"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteCertificate(cert.id)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete certificate"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
            <p className="text-sm" style={{ color: '#5C635A' }}>
              <strong>Tip:</strong> A complete profile with a photo and verified certificates helps build trust with students 
              and can increase enrollment in your courses.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TeacherProfilePage;
